import { Numpad, type NumpadKey } from '@mobile/components/kartoy/numpad'
import { type ComponentRef, useEffect, useRef } from 'react'
import { Platform, TextInput, type TextInputProps } from 'react-native'

import { useCreateStyles } from './styles'

type KeyboardInputProps = {
  value: string
  onChange: (value: string) => void
  isDecimalKeyVisible?: boolean
  inputProps?: Omit<TextInputProps, 'value' | 'onChangeText'>
}

// Невидимый ввод, открывающий системную клавиатуру (native).
// На web показываем кастомный numpad kartoy и отключаем системную клавиатуру.
export function KeyboardInput({ value, onChange, isDecimalKeyVisible = true, inputProps }: KeyboardInputProps) {
  const styles = useCreateStyles()
  const inputRef = useRef<ComponentRef<typeof TextInput>>(null)

  // Физическая клавиатура работает и после потери фокуса скрытым полем.
  useEffect(() => {
    if (Platform.OS !== 'web') return

    // Не перехватываем сочетания клавиш и ввод в других редактируемых полях.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return

      const target = event.target

      if (
        target instanceof HTMLElement &&
        target !== (inputRef.current as unknown) &&
        (target.closest('input, textarea, select') || target.isContentEditable)
      ) {
        return
      }

      const key = event.key

      if (key && /^\d$/.test(key)) {
        event.preventDefault()
        onChange(value + key)
      } else if (key === 'Backspace') {
        event.preventDefault()
        onChange(value.slice(0, -1))
      } else if (isDecimalKeyVisible && (key === ',' || key === '.')) {
        event.preventDefault()
        onChange(`${value},`)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDecimalKeyVisible, onChange, value])

  if (Platform.OS === 'web') {
    const handleKey = (key: NumpadKey) => {
      if (key === 'backspace') {
        onChange(value.slice(0, -1))

        return
      }

      onChange(value + key)
    }

    return (
      <>
        <TextInput
          autoFocus
          value={value}
          onChangeText={onChange}
          ref={inputRef}
          style={styles.hiddenInput}
          {...inputProps}
          inputMode="none"
          showSoftInputOnFocus={false}
        />
        <Numpad
          onPressKey={handleKey}
          onKeyPress={() => inputRef.current?.focus()}
          rowGap={0}
          isDecimalKeyVisible={isDecimalKeyVisible}
        />
      </>
    )
  }

  return (
    <TextInput
      autoFocus
      caretHidden
      value={value}
      onChangeText={onChange}
      keyboardType="number-pad"
      style={styles.hiddenInput}
      {...inputProps}
    />
  )
}
